import { useQuery } from '@apollo/client';
import { GetOrganizationsListDocument } from '../../../graphql/query/organizations/GetOrganizationsList.generated.tsx';
import { useOptionsByPathQuery } from '../../../hooks/useOptionsByPathQuery.ts';
import type { AdvertiserOption, ContentCategoryOption } from '../types.ts';

const OPENAUDIENCE = '1613149491';
const BIDDER_CREATIVE_IAB_CATEGORIES_PATH = 'bidder_creative.iab_categories';

type UseFetchRequiredOptionsParams = {
  includeIabCategories?: boolean;
};

const isContentCategoryOption = (option: ContentCategoryOption | null): option is ContentCategoryOption =>
  option !== null;

/**
 * Custom hook to fetch required options for creative forms
 * Fetches organizations/advertisers
 */
export const useFetchRequiredOptions = ({
  includeIabCategories = false,
}: UseFetchRequiredOptionsParams = {}): {
  advertiserOptions: AdvertiserOption[];
  iabCategoryOptions: ContentCategoryOption[];
  isLoading: boolean;
} => {
  const { data: organizationsData, loading: organizationsLoading } = useQuery(GetOrganizationsListDocument, {
    variables: {
      where: {
        _and: [
          { exchange_account_network: { _is_null: false } },
          { exchange_account_network_id: { _neq: OPENAUDIENCE } },
        ],
      },
    },
  });

  const { data: iabCategoriesData, loading: iabCategoriesLoading } = useOptionsByPathQuery({
    variables: { path: BIDDER_CREATIVE_IAB_CATEGORIES_PATH },
    skip: !includeIabCategories,
  });

  return {
    advertiserOptions: organizationsData?.account || [],
    iabCategoryOptions: includeIabCategories
      ? iabCategoriesData?.optionsByPath.filter(isContentCategoryOption) || []
      : [],
    isLoading: organizationsLoading || (includeIabCategories && iabCategoriesLoading),
  };
};
